import React from 'react';
import { Card, CardBody, CardHeader, Button, Input } from '../../components/ui';
import { createDiscussionTag, listTags, renameTag, setTagActive, updateTagPlacement, updateTagTooltip } from '../../lib/firestore';
import {
  DISCUSSION_CATEGORY_ROW_LABELS,
  discussionTagDisplayOrder,
  discussionTagDisplayRow,
  sortDiscussionTagsForDisplay
} from '../../lib/discussionCategories';
import type { Tag } from '../../lib/types';
import { useAuth } from '../../auth/AuthProvider';

const START_OF_ROW = '__start__';
const END_OF_ROW = '__end__';

const SELECT_CLASS =
  'w-full rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30';

function isActive(tag: Tag) {
  return tag.isActive !== false;
}

function rowLabel(row: number) {
  return DISCUSSION_CATEGORY_ROW_LABELS[row - 1] ?? `Row ${row}`;
}

function availableRows(tags: Tag[]) {
  const maxExistingRow = tags.reduce((max, tag) => Math.max(max, discussionTagDisplayRow(tag)), 0);
  const maxRow = Math.max(DISCUSSION_CATEGORY_ROW_LABELS.length, maxExistingRow);
  return Array.from({ length: maxRow }, (_, index) => index + 1);
}

function groupedActiveRows(tags: Tag[]) {
  const rowNumbers = availableRows(tags);
  const activeTags = sortDiscussionTagsForDisplay(tags.filter(isActive));

  return rowNumbers.map((row) => ({
    row,
    label: rowLabel(row),
    tags: activeTags.filter((tag) => discussionTagDisplayRow(tag) === row)
  }));
}

function displayOrderAfter(rowTags: Tag[], afterId: string) {
  if (!rowTags.length) return 10;
  if (afterId === START_OF_ROW) return discussionTagDisplayOrder(rowTags[0]) - 10;

  const afterIndex = rowTags.findIndex((tag) => tag.id === afterId);
  if (afterIndex < 0 || afterId === END_OF_ROW) {
    return discussionTagDisplayOrder(rowTags[rowTags.length - 1]) + 10;
  }

  const currentOrder = discussionTagDisplayOrder(rowTags[afterIndex]);
  const nextTag = rowTags[afterIndex + 1];
  if (!nextTag) return currentOrder + 10;
  return (currentOrder + discussionTagDisplayOrder(nextTag)) / 2;
}

export default function TagsAdminPage() {
  const { user } = useAuth();
  const [tags, setTags] = React.useState<Tag[]>([]);
  const [newName, setNewName] = React.useState('');
  const [newTooltip, setNewTooltip] = React.useState('');
  const [newRow, setNewRow] = React.useState(DISCUSSION_CATEGORY_ROW_LABELS.length);
  const [insertAfterId, setInsertAfterId] = React.useState(END_OF_ROW);
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const rowOptions = React.useMemo(() => availableRows(tags), [tags]);
  const rows = React.useMemo(() => groupedActiveRows(tags), [tags]);
  const retiredTags = React.useMemo(() => sortDiscussionTagsForDisplay(tags.filter((tag) => !isActive(tag))), [tags]);
  const selectedRowTags = rows.find((row) => row.row === newRow)?.tags ?? [];

  async function refresh() {
    const all = await listTags('discussion', false);
    setTags(all);
  }

  React.useEffect(() => {
    refresh().catch((e) => setError(String(e)));
  }, []);

  async function addTag() {
    if (!newName.trim() || !user?.sub) return;
    setError(null);
    setBusyId('add');
    try {
      await createDiscussionTag(newName, user.sub, {
        tooltip: newTooltip,
        displayRow: newRow,
        displayOrder: displayOrderAfter(selectedRowTags, insertAfterId)
      });
      setNewName('');
      setNewTooltip('');
      setInsertAfterId(END_OF_ROW);
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function toggle(tag: Tag) {
    setError(null);
    setBusyId(tag.id);
    try {
      await setTagActive(tag.id, tag.isActive === false);
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function doRename(tagId: string, name: string) {
    setError(null);
    setBusyId(tagId);
    try {
      await renameTag(tagId, name);
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function doPlacement(tagId: string, displayRow: number, displayOrder: number) {
    setError(null);
    setBusyId(tagId);
    try {
      await updateTagPlacement(tagId, displayRow, displayOrder);
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function doTooltip(tagId: string, tooltip: string) {
    setError(null);
    setBusyId(tagId);
    try {
      await updateTagTooltip(tagId, tooltip);
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4">
      <Card>
        <CardHeader
          title="Admin - Discussion Tags"
          subtitle="Add, place, rename, or retire discussion categories"
        />
        <CardBody className="space-y-4">
          {error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
          ) : null}

          <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3">
            <div className="grid grid-cols-1 gap-2 xl:grid-cols-[minmax(180px,1fr),minmax(220px,1.25fr),180px,220px,auto] xl:items-end">
              <label className="grid gap-1">
                <span className="text-xs font-semibold text-zinc-600">New tag</span>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="New tag name (e.g. Service Management)"
                />
              </label>
              <label className="grid gap-1">
                <span className="text-xs font-semibold text-zinc-600">Tool tip</span>
                <Input
                  value={newTooltip}
                  onChange={(e) => setNewTooltip(e.target.value)}
                  placeholder="Optional hover text for this category"
                />
              </label>
              <label className="grid gap-1">
                <span className="text-xs font-semibold text-zinc-600">Display row</span>
                <select
                  value={newRow}
                  onChange={(event) => {
                    setNewRow(Number(event.target.value));
                    setInsertAfterId(END_OF_ROW);
                  }}
                  className={SELECT_CLASS}
                >
                  {rowOptions.map((row) => (
                    <option key={row} value={row}>
                      {rowLabel(row)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-1">
                <span className="text-xs font-semibold text-zinc-600">Position</span>
                <select value={insertAfterId} onChange={(event) => setInsertAfterId(event.target.value)} className={SELECT_CLASS}>
                  <option value={START_OF_ROW}>Start of row</option>
                  {selectedRowTags.map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      After {tag.name}
                    </option>
                  ))}
                  <option value={END_OF_ROW}>End of row</option>
                </select>
              </label>
              <Button onClick={addTag} disabled={!newName.trim() || busyId === 'add'} className="w-full lg:w-auto">
                {busyId === 'add' ? 'Adding...' : 'Add tag'}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {rows.map((row) => (
              <div key={row.row} className="rounded-2xl border border-zinc-200 bg-white">
                <div className="border-b border-zinc-200 bg-zinc-50 px-3 py-2">
                  <div className="text-sm font-semibold text-zinc-900">{row.label}</div>
                  <div className="mt-0.5 text-xs text-zinc-500">
                    Row {row.row} - {row.tags.length} active tag{row.tags.length === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="divide-y divide-zinc-200">
                  {row.tags.length ? (
                    row.tags.map((tag) => (
                      <TagRow
                        key={tag.id}
                        tag={tag}
                        rowOptions={rowOptions}
                        busy={busyId === tag.id}
                        onToggle={() => toggle(tag)}
                        onRename={doRename}
                        onPlacement={doPlacement}
                        onTooltip={doTooltip}
                      />
                    ))
                  ) : (
                    <div className="px-3 py-3 text-sm text-zinc-600">No active tags in this row.</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {retiredTags.length ? (
            <div className="rounded-2xl border border-zinc-200 bg-white">
              <div className="border-b border-zinc-200 bg-zinc-50 px-3 py-2">
                <div className="text-sm font-semibold text-zinc-900">Retired tags</div>
                <div className="mt-0.5 text-xs text-zinc-500">Hidden from discussion category selectors.</div>
              </div>
              <div className="divide-y divide-zinc-200">
                {retiredTags.map((tag) => (
                  <TagRow
                    key={tag.id}
                    tag={tag}
                    rowOptions={rowOptions}
                    busy={busyId === tag.id}
                    onToggle={() => toggle(tag)}
                    onRename={doRename}
                    onPlacement={doPlacement}
                    onTooltip={doTooltip}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}

function TagRow({
  tag,
  rowOptions,
  busy,
  onToggle,
  onRename,
  onPlacement,
  onTooltip
}: {
  tag: Tag;
  rowOptions: number[];
  busy: boolean;
  onToggle: () => void;
  onRename: (id: string, name: string) => void;
  onPlacement: (id: string, displayRow: number, displayOrder: number) => void;
  onTooltip: (id: string, tooltip: string) => void;
}) {
  const [name, setName] = React.useState(tag.name ?? '');
  const [tooltip, setTooltip] = React.useState(tag.tooltip ?? '');
  const [displayRow, setDisplayRow] = React.useState(String(discussionTagDisplayRow(tag)));
  const [displayOrder, setDisplayOrder] = React.useState(String(discussionTagDisplayOrder(tag)));

  React.useEffect(() => {
    setName(tag.name ?? '');
    setTooltip(tag.tooltip ?? '');
    setDisplayRow(String(discussionTagDisplayRow(tag)));
    setDisplayOrder(String(discussionTagDisplayOrder(tag)));
  }, [tag]);

  const active = tag.isActive !== false;
  const nextRow = Number(displayRow);
  const nextOrder = Number(displayOrder);
  const placementChanged =
    nextRow !== discussionTagDisplayRow(tag) || nextOrder !== discussionTagDisplayOrder(tag);
  const placementValid = Number.isFinite(nextRow) && nextRow > 0 && Number.isFinite(nextOrder);
  const tooltipChanged = tooltip.trim() !== (tag.tooltip ?? '').trim();

  return (
    <div className="grid grid-cols-1 gap-3 p-3 xl:grid-cols-[minmax(220px,1fr),minmax(260px,1fr),auto] xl:items-center">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className={`break-words text-sm font-semibold ${active ? 'text-zinc-900' : 'text-zinc-500 line-through'}`}>
            {tag.name}
          </div>
          <span className="rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs text-zinc-700">
            {tag.slug ?? tag.id}
          </span>
          <span className={`text-xs ${active ? 'text-green-700' : 'text-zinc-600'}`}>{active ? 'Active' : 'Retired'}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr),auto,150px,110px,auto] md:items-center">
          <Input value={name} onChange={(e) => setName(e.target.value)} aria-label={`Rename ${tag.name}`} />
          <Button
            variant="secondary"
            onClick={() => onRename(tag.id, name)}
            disabled={busy || !name.trim() || name.trim() === (tag.name ?? '').trim()}
          >
            Rename
          </Button>
          <select value={displayRow} onChange={(event) => setDisplayRow(event.target.value)} className={SELECT_CLASS}>
            {rowOptions.map((row) => (
              <option key={row} value={row}>
                {rowLabel(row)}
              </option>
            ))}
          </select>
          <Input
            type="number"
            step="any"
            value={displayOrder}
            onChange={(event) => setDisplayOrder(event.target.value)}
            aria-label={`Display order for ${tag.name}`}
          />
          <Button
            variant="outline"
            onClick={() => onPlacement(tag.id, Math.trunc(nextRow), nextOrder)}
            disabled={busy || !placementChanged || !placementValid}
          >
            Place
          </Button>
        </div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr),auto] md:items-center">
          <Input
            value={tooltip}
            onChange={(event) => setTooltip(event.target.value)}
            aria-label={`Tool tip for ${tag.name}`}
            placeholder="Optional tool tip text"
          />
          <Button
            variant="outline"
            onClick={() => onTooltip(tag.id, tooltip)}
            disabled={busy || !tooltipChanged}
          >
            Save tip
          </Button>
        </div>
      </div>

      <div className="flex justify-end">
        <Button variant="ghost" onClick={onToggle} disabled={busy}>
          {active ? 'Retire' : 'Re-activate'}
        </Button>
      </div>
    </div>
  );
}
